// Browser compatibility polyfills for Samsung Internet and other older browsers

// Polyfill for Object.assign if not available
if (typeof Object.assign !== 'function') {
  Object.assign = function(target: any, ...sources: any[]) {
    if (target == null) {
      throw new TypeError('Cannot convert undefined or null to object')
    }
    
    const to = Object(target)
    
    for (let index = 1; index < arguments.length; index++) {
      const nextSource = arguments[index]
      
      if (nextSource != null) {
        for (const nextKey in nextSource) {
          if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
            to[nextKey] = nextSource[nextKey]
          }
        }
      }
    }
    return to
  }
}

// Polyfill for Array.includes if not available
if (!Array.prototype.includes) {
  Array.prototype.includes = function(searchElement: any, fromIndex?: number) {
    if (this == null) {
      throw new TypeError('"this" is null or not defined')
    }
    
    const o = Object(this)
    const len = o.length >>> 0
    
    if (len === 0) {
      return false
    }
    
    let n = fromIndex ?? 0
    let k = Math.max(n >= 0 ? n : len + n, 0)
    
    while (k < len) {
      if (o[k] === searchElement) {
        return true
      }
      k++
    }
    return false
  }
}

// Polyfill for String.includes if not available
if (!String.prototype.includes) {
  String.prototype.includes = function(search: string, start?: number) {
    if (typeof start !== 'number') {
      start = 0
    }
    
    if (start + search.length > this.length) {
      return false
    } else {
      return this.indexOf(search, start) !== -1
    }
  }
}

// Polyfill for Promise.finally if not available
if (Promise && !Promise.prototype.finally) {
  Promise.prototype.finally = function(onFinally: () => void) {
    return this.then(
      (value) => Promise.resolve(onFinally()).then(() => value),
      (reason) => Promise.resolve(onFinally()).then(() => { throw reason })
    )
  }
}

// Polyfill for requestAnimationFrame if not available
if (!window.requestAnimationFrame) {
  window.requestAnimationFrame = function(callback: FrameRequestCallback): number {
    return setTimeout(() => callback(Date.now()), 16) as unknown as number
  }
}

if (!window.cancelAnimationFrame) {
  window.cancelAnimationFrame = function(id: number): void {
    clearTimeout(id)
  }
}

// Polyfill for AudioContext if not available
if (!window.AudioContext && !(window as any).webkitAudioContext) {
  console.warn('AudioContext not supported - audio features may not work')
}

// Polyfill for Web Audio API
if (!window.AudioContext && (window as any).webkitAudioContext) {
  (window as any).AudioContext = (window as any).webkitAudioContext
}

// Polyfill for getUserMedia if not available
if (!navigator.mediaDevices) {
  (navigator as any).mediaDevices = {}
}

if (!navigator.mediaDevices.getUserMedia) {
  navigator.mediaDevices.getUserMedia = function(constraints) {
    const getUserMedia = (navigator as any).webkitGetUserMedia || (navigator as any).mozGetUserMedia
    
    if (!getUserMedia) {
      return Promise.reject(new Error('getUserMedia is not implemented in this browser'))
    }
    
    return new Promise((resolve, reject) => {
      getUserMedia.call(navigator, constraints, resolve, reject)
    })
  }
}

// Polyfill for CSS.supports if not available
if (!window.CSS || !window.CSS.supports) {
  (window as any).CSS = {
    supports: function(property: string, value?: string) {
      // Simple fallback - return false for unsupported features
      return false
    }
  }
}

// Polyfill for IntersectionObserver if not available
if (!window.IntersectionObserver) {
  console.warn('IntersectionObserver not supported - some animations may not work')
}

// Polyfill for ResizeObserver if not available
if (!window.ResizeObserver) {
  console.warn('ResizeObserver not supported - some responsive features may not work')
}

// Polyfill for matchMedia if not available
if (!window.matchMedia) {
  (window as any).matchMedia = function(query: string) {
    return {
      matches: false,
      media: query,
      onchange: null,
      addListener: function() {},
      removeListener: function() {},
      addEventListener: function() {},
      removeEventListener: function() {},
      dispatchEvent: function() { return false }
    }
  }
}

// Export browser detection utilities
export const browserUtils = {
  // Detect Samsung Internet Browser
  isSamsungInternet: () => {
    const userAgent = navigator.userAgent
    return /SamsungBrowser/i.test(userAgent) || 
           (userAgent.includes('Android') && userAgent.includes('Samsung'))
  },
  
  // Detect if browser supports modern features
  supportsModernFeatures: () => {
    return !!(typeof window.Promise !== 'undefined' && 
              typeof window.fetch !== 'undefined' && 
              typeof window.localStorage !== 'undefined' && 
              typeof window.sessionStorage !== 'undefined' &&
              typeof window.requestAnimationFrame !== 'undefined' &&
              typeof window.AudioContext !== 'undefined')
  },
  
  // Get browser name
  getBrowserName: () => {
    const userAgent = navigator.userAgent
    if (userAgent.includes('Chrome')) return 'Chrome'
    if (userAgent.includes('Firefox')) return 'Firefox'
    if (userAgent.includes('Safari')) return 'Safari'
    if (userAgent.includes('Edge')) return 'Edge'
    if (browserUtils.isSamsungInternet()) return 'Samsung Internet'
    return 'Unknown'
  },
  
  // Check if speech recognition is supported
  supportsSpeechRecognition: () => {
    return !!(typeof window.SpeechRecognition !== 'undefined' || typeof (window as any).webkitSpeechRecognition !== 'undefined')
  }
} 